// Angular
import { Observable, combineLatest, filter, map, of, shareReplay, startWith, takeUntil, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';

// Services
import { DataCachingService, LocationService } from '../../../../shared/services';
import { CompanyService } from '../../../company/services/company.service';
import { JobService } from '../../services/job.service';
import { BopService } from '../../../asset/bop/services/bop.service';
import { UnitService } from '../../../asset/unit/services/unit.service';
import { LookupService } from '../../../../shared/services/lookup.service';

// Libraries
import { MatDialog } from '@angular/material/dialog';

import { Select } from '@ngxs/store';

// Models
import { UnitPeriodDto } from '../../models/unit-period-dto.model';
import { UnitViewModel } from '../../../asset/unit/models/unit-view.model';
import { Job } from '../../models';
import { EditJobInformationPayload } from '../../models/edit-job-information-payload';
import { CompanyListModel } from '../../../company/models/company-view-model.model';
import { JobScopeDto } from '../../models/job-scope-dto.model';
import { AssetViewModel } from '../../../asset/string/models/string-view.model';
import { JobTypeDto } from '../../components/daily-report/models/job-type-dto';
import { AppCleanup, LookUpResponse } from '../../../../shared/models';

// Enums
import { AssetType, JobType, UnitType } from '../../../../shared/enums';
import { BopTestType } from '../../models/bop-test-type.enum';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';

// Components
import { AddLocationDialogComponent } from '../../../../dialogs/add-location-dialog/add-location-dialog.component';

// TODO: This should eventually become a component that encapsulates the shared forms, state and observables of JobEditDialogComponent and JobStartDialogComponent.
@Injectable({
	providedIn: 'root',
})
export class JobInfoDialogService extends AppCleanup {
	constructor(
		private lookupService: LookupService,
		private dataCachingService: DataCachingService,
		private companyService: CompanyService,
		private jobService: JobService,
		private bopService: BopService,
		private unitService: UnitService,
		private locationService: LocationService,
		private dialog: MatDialog
	) {
		super();
	}

	createJobInfoDialogObservables(unitPeriods: UnitPeriodDto[], facilityTestBopFormControl: FacilityTestBopFormControl): JobInfoDialogInitialObservables {
		const currentUnit: UnitViewModel | undefined = unitPeriods[0]?.unit;
		const unitType: UnitType | undefined = currentUnit?.unitTypeId;

		const dataIsReady$ = this.dataCachingService.dataReady$.pipe(
			// hack.. sometimes dataReady is not ready in time. Refresh if so.
			// TODO: this should be fixed in the dataCachingService (use ngrx here?)
			tap((isDataReady) => {
				if (!isDataReady) this.dataCachingService.refreshDataCache();
			}),
			filter((isDataReady) => isDataReady),
			shareReplay(1)
		);

		const jobTypes$ = dataIsReady$.pipe(
			map(() => this.dataCachingService.getUnitTypeAllowedJobTypes(unitType)),
			shareReplay(1)
		);

		// const jobTypes$ = of(this.lookupService.getUnitTypeAllowedJobTypes(unitType));

		const companiesList$ = this.companyService.companiesListReady$.pipe(
			// hack.. sometimes companies are not ready in time. Refresh if so.
			// TODO: this should be fixed in the companyService. (use ngrx here?)
			tap((companies: CompanyListModel[]) => {
				if (!companies) this.companyService.refreshCompanies();
			}),
			filter((companies: CompanyListModel[]) => !!companies),
			shareReplay(1)
		);

		const jobsToStreamTo$ =
			unitPeriods.length === 1
				? this.jobService.getJobsToStreamTo(unitPeriods[0]?.id).pipe(
						map((jobsToStreamTo) => jobsToStreamTo.map((j) => new Job(j))),
						shareReplay(1)
					)
				: of([]);

		const allBops$ = this.bopService.getBops().pipe(
			map((bops) => bops.filter((bop) => bop.assetTypeId !== AssetType.Bop)),
			map((bops) => assetsToJobInfoBopViewModels(bops)),
			shareReplay(1)
		);

		const unitBops$ = currentUnit?.guid
			? this.unitService.getUnitBops(currentUnit?.guid).pipe(
					map((bops) => bops.filter((bop) => bop.assetTypeId !== AssetType.Bop)),
					map((bops) => assetsToJobInfoBopViewModels(bops)),
					shareReplay(1)
				)
			: of([]);

		const allJobScopes$ = dataIsReady$.pipe(
			map(() => this.dataCachingService.jobScopes),
			shareReplay(1)
		);

		const facilityTestBopFormControlValueChanges$ = facilityTestBopFormControl.valueChanges.pipe(startWith(facilityTestBopFormControl.value), shareReplay(1));

		const searchResultBops$ = combineLatest([allBops$, facilityTestBopFormControlValueChanges$]).pipe(
			map(([allBops, facilityTestBopFormControlValue]) => {
				// Not a text search string, so we know the user has selected a bop from the dropdown.
				if (typeof facilityTestBopFormControlValue !== 'string') return [];

				// The user has entered a text search string, so we need to filter the bops.
				return UtilityHelper.filterSortByTextSimilarity(allBops, (bop) => bop.displayName.trim().toLowerCase(), facilityTestBopFormControlValue.trim().toLowerCase());
			}),
			shareReplay(1)
		);

		return {
			dataIsReady$,
			jobTypes$,
			companiesList$,
			jobsToStreamTo$,
			allBops$,
			unitBops$,
			allJobScopes$,
			searchResultBops$,
		};
	}

	handleLocationChanges(locationIdControl: FormControl<number | null>, disposeOn: Observable<void>) {
		locationIdControl.valueChanges.pipe(takeUntil(disposeOn)).subscribe((value) => {
			if (value === 0) {
				locationIdControl.setValue(null, { emitEvent: false });

				const dialogRef = this.dialog.open(AddLocationDialogComponent, {});

				dialogRef
					.afterClosed()
					.pipe(takeUntil(disposeOn))
					.subscribe((locationName) => {
						locationName = locationName.trim();

						this.locationService.addLocation({ id: 0, name: locationName }).subscribe((locationDto) => {
							if (locationDto) locationIdControl.setValue(locationDto.id);
						});
					});
			}
		});
	}
}

export function bopControlToJobAssets(bopControl: FacilityTestBopFormControl | FieldTestBopsFormControl | null | undefined, jobId: number): JobAssetDto[] {
	if (bopControl?.value == null) return [];
	if (typeof bopControl.value === 'string') return [];

	if (Array.isArray(bopControl.value))
		return bopControl.value.map((bop) => ({
			jobId: jobId,
			assetGuid: bop.guid,
			isDeleted: false,
		}));

	return [
		{
			jobId: jobId,
			assetGuid: bopControl.value.guid,
			isDeleted: false,
		},
	];
}

export function jobTypeToBopTestType(jobType: JobType): BopTestType {
	switch (jobType) {
		case JobType.BopFacilityTest:
			return BopTestType.FacilityTest;
		case JobType.CoiledTubing:
		case JobType.Snubbing:
			return BopTestType.FieldTest;
		default:
			return BopTestType.None;
	}
}

export function assetsToJobInfoBopViewModels(assets: AssetViewModel[]) {
	if (assets) {
		return assets.map((bop) => {
			const g: JobInfoDialogBopViewModel = {
				...bop,
				displayName: getBopDisplayName(bop),
			};
			return g;
		});
	}
}

export function getBopDisplayName(bop: Pick<AssetViewModel, 'district' | 'name' | 'description'>): string {
	return [bop.district, bop.name, bop.description].filter((value) => value).join(' ');
}

export function validateAreBopObjects(control: AbstractControl): ValidationErrors | null {
	const bops = control.value;
	if (bops == null) return null;

	for (let bop of bops) {
		if (!isBopObject(bop)) {
			return {
				notBopObject: 'At least one of the selected BOPs is not a valid BOP object',
			};
		}
	}

	return null;
}

export function validateIsBopObject(control: AbstractControl): ValidationErrors | null {
	const bop = control.value;

	if (!isBopObject(bop)) {
		return { notBopObject: 'Selected BOP is not a valid BOP object' };
	}

	return null;
}

export function compareBopsByGuid(a: Pick<AssetViewModel, 'guid'>, b: Pick<AssetViewModel, 'guid'>) {
	return a.guid === b.guid;
}

function isBopObject(bop: any) {
	return typeof bop === 'object' && bop !== null && 'guid' in bop;
}

export function unitTypeToJobType(unitType: UnitType | null | undefined): JobType {
	switch (unitType) {
		case UnitType.CoiledTubing:
			return JobType.CoiledTubing;
		case UnitType.Snubbing:
			return JobType.Snubbing;
		case UnitType.ChemMixing:
			return JobType.ChemMixing;
		case UnitType.FluidPump:
			return JobType.FluidPump;
		case UnitType.NitrogenPump:
			return JobType.NitrogenPump;
		case UnitType.FacilityTest:
			return JobType.BopFacilityTest;
		default:
			return JobType.Other;
	}
}

export type JobInfoDialogInitialObservables = {
	dataIsReady$: Observable<boolean>;
	jobTypes$: Observable<JobTypeDto[]>;
	companiesList$: Observable<CompanyListModel[]>;
	jobsToStreamTo$: Observable<Job[]>;
	allBops$: Observable<JobInfoDialogBopViewModel[]>;
	unitBops$: Observable<JobInfoDialogBopViewModel[]>;
	allJobScopes$: Observable<JobScopeDto[]>;
	searchResultBops$: Observable<JobInfoDialogBopViewModel[]>;
};

export interface JobInfoDialogInputData {
	key?: string;
	name?: string;
	wellName?: string;
	wellNumber?: string;
	jobScopeId?: number;
	locationId?: number;
	hide: boolean;
	companyId: number;
	jobTypeId?: number;
	jobStatusId?: number;
	dateStarted?: Date;
	utcDateTimeOffset: number;
	unitPeriods: UnitPeriodDto[];
	bops: AssetViewModel[];
}

export type JobEditDialogOutputData = EditJobAction | StreamJobAction;

export interface EditJobAction {
	action: 'edit-job';
	editJobInformationPayload: EditJobInformationPayload;
	save: boolean;
}

export interface StreamJobAction {
	action: 'stream-job';
	jobToStreamToId: number;
	unitPeriodId: number;
}

export interface JobAssetDto {
	jobId: number;
	assetGuid: string;
	isDeleted: boolean;
}

export interface JobDialogFormControlCache {
	streamToExistingJob: FormControl<boolean>;
	jobToStreamToId: FormControl<number | null>;

	jobTypeId: FormControl<number | null>;

	facilityTestBop: FacilityTestBopFormControl;
	fieldTestBops: FieldTestBopsFormControl;

	companyId: FormControl<number | null>;
	wellName: FormControl<string | null>;
	wellNumber: FormControl<string | null>;
	scope: FormControl<number | null>;
	locationId: FormControl<number | null>;
	dateStarted: FormControl<Date | null>;
	utcDateTimeOffset: FormControl<number | null>;
	hide: FormControl<boolean>;
}

export interface JobDialogFormControls {
	jobTypeId: FormControl<number | null>;
	companyId: FormControl<number | null>;
	wellName: FormControl<string | null>;
	wellNumber: FormControl<string | null>;
	scope: FormControl<number | null>;
	locationId: FormControl<number | null>;
	dateStarted: FormControl<Date | null>;
	utcDateTimeOffset: FormControl<number | null>;

	streamToExistingJob?: FormControl<boolean>;
	bop?: FacilityTestBopFormControl | FieldTestBopsFormControl;
	hide?: FormControl<boolean>;
}

export interface JobDialogStreamFormControls {
	streamToExistingJob: FormControl<boolean>;
	jobToStreamToId: FormControl<number | null>;
}

export interface JobInfoDialogBopViewModel extends Pick<AssetViewModel, 'guid' | 'name' | 'description' | 'district'> {
	displayName: string;
}

export type FieldTestBopsFormControl = FormControl<JobInfoDialogBopViewModel[] | null>;

export type FacilityTestBopFormControl = FormControl<JobInfoDialogBopViewModel | string | null>;
